import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys } from '@/lib/queryKeys';
import type { TaxZone } from '@/types/tax';
import { hasAnyPopulatingZone } from '@/components/organisms/taxes/support/tax-helpers';

/**
 * When any zone is in the `populating` placeholder state (freshly added
 * via bulk service-area save, waiting for the Ziptax populate worker
 * to upsert the real row), refetch the list every 5 seconds so the
 * "Pending" badge transitions to the rate-or-error state without the
 * user having to manually refresh. Cache hits return in ~100ms — the
 * UI typically sees the flip within one or two polls.
 *
 * Polling pauses when:
 *   - no zone is still populating (the common steady state);
 *   - the maximum poll window has elapsed (`MAX_POLL_MS`). After the
 *     cap, the row stays at "Pending" until the user navigates away
 *     and back, or the route's manual refresh button is clicked. The
 *     cap prevents a stuck row (e.g. Ziptax outage past the BullMQ
 *     attempts ceiling) from polling forever.
 */
const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_MS = 90_000;

export function useTaxZones() {
  return useApiQuery<TaxZone[]>({
    queryKey: taxZoneKeys.list(),
    queryFn: () => apiGet<TaxZone[]>(operationsClient, taxZoneEndpoints.list()),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!Array.isArray(data)) return false;
      if (!hasAnyPopulatingZone(data)) return false;

      // React Query stamps `dataUpdatedAt` on every successful fetch.
      // The first successful fetch in this query's lifecycle (since
      // last invalidation) anchors the cap: 90s of "still populating"
      // means we give up and stop polling. The anchor resets when an
      // external trigger (a fresh bulk save) invalidates the query
      // cache, which is exactly the moment a new round of populating
      // rows would appear.
      const startedAt = query.state.dataUpdatedAt;
      const elapsed = startedAt > 0 ? Date.now() - startedAt : 0;
      if (elapsed > MAX_POLL_MS) return false;
      return POLL_INTERVAL_MS;
    },
  });
}
